import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { MenuOptionValue, Paginated, PaginationQuery, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { OtherCarAssignment, OtherCarAssignmentVersion, UpsertOtherCarAssignment } from "./types";

export const useOtherCarAssignmentApi = () => {
  const api = useApi();

  const getAdminOtherCarAssignment = useCallback(
    (otherAssignmentId: string, signal: AbortSignal) => {
      const url = `/admin/other_car_assignments/${otherAssignmentId}`;
      return useSWRMutation<OtherCarAssignment | null, CustomError, string>(url, (url) =>
        api.get<OtherCarAssignment>({ url, signal })
      );
    },
    [api]
  );

  const useAdminOtherCarAssignment = useCallback(
    (query: PaginationQuery, signal: AbortSignal, carId?: string) => {
      const url = carId ? buildUrl(`/admin/cars/${carId}/other_car_assignments`, query) : "";
      return useSWR<Paginated<OtherCarAssignment> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<OtherCarAssignment>>({ url, signal })
      );
    },
    [api]
  );

  const insertOtherCarAssignment = useCallback(
    async (body: UpsertOtherCarAssignment): Promise<{ id: string }> => {
      const url = `/admin/other_car_assignments`;
      const id = await api.post<UpsertOtherCarAssignment, { id: string }>(url, body);
      api.mutateCache([
        url,
        `/admin/other_car_assignments/${id}`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/drivers`,
        `/admin/cars/${body.carId}/other_car_assignments`,
        `/admin/cars/${body.carId}/tasks`,
        `/admin/car_tasks/status`,
      ]);
      return id;
    },
    [api]
  );

  const updateOtherCarAssignment = useCallback(
    async (id: string, body: UpsertOtherCarAssignment) => {
      const url = `/admin/other_car_assignments/${id}`;
      await api.put<UpsertOtherCarAssignment>(url, body);
      api.mutateCache([
        url,
        `/admin/other_car_assignments`,
        `/admin/other_car_assignments/${id}/versions`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/drivers`,
        `/admin/cars/${body.carId}/other_car_assignments`,
        `/admin/cars/${body.carId}/tasks`,
        `/admin/car_tasks/status`,
      ]);
    },
    [api]
  );

  const deleteOtherCarAssignment = useCallback(
    async (otherCarAssignment: OtherCarAssignment): Promise<void> => {
      const url = `/admin/other_car_assignments/${otherCarAssignment.id}`;
      await api.del(url).then(() => {
        api.mutateCache([
          url,
          `/admin/other_car_assignments`,
          `/admin/cars/${otherCarAssignment.car.id}`,
          `/admin/cars/${otherCarAssignment.car.id}/overview`,
          `/admin/cars/${otherCarAssignment.car.id}/drivers`,
          `/admin/cars/${otherCarAssignment.car.id}/other_car_assignments`,
          `/admin/cars/${otherCarAssignment.car.id}/tasks`,
          `/admin/car_tasks/status`,
        ]);
      });
    },
    [api]
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/other_car_assignments/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal })
      );
    },
    [api]
  );

  const addToOtherCarAssignment = useCallback(
    async (body: UpsertOtherCarAssignment): Promise<{ id: string }> => {
      const url = `/admin/cars/${body.carId}/add_to_other_assignment`;
      const id = await api.post<UpsertOtherCarAssignment, { id: string }>(url, body);
      api.mutateCache([
        url,
        `/admin/other_car_assignments/${id}`,
        `/admin/other_car_assignments`,
        `/admin/cars`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/drivers`,
        `/admin/cars/${body.carId}/other_car_assignments`,
        `/admin/cars/${body.carId}/tasks`,
        `/admin/car_tasks/status`,
      ]);
      return id;
    },
    [api]
  );

  const getOtherCarAssignmentHistory = useCallback(
    (query: PaginationQuery, otherAssignmentId: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/other_car_assignments/${otherAssignmentId}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal })
      );
    },
    [api]
  );

  const getOtherCarAssignmentVersion = useCallback(
    (otherAssignmentId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/other_car_assignments/${otherAssignmentId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<OtherCarAssignmentVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<OtherCarAssignmentVersion>>({ url, signal })
      );
    },
    [api]
  );

  return {
    updateOtherCarAssignment,
    deleteOtherCarAssignment,
    useAdminOtherCarAssignment,
    insertOtherCarAssignment,
    getAdminOtherCarAssignment,
    getFieldFilters,
    addToOtherCarAssignment,
    getOtherCarAssignmentHistory,
    getOtherCarAssignmentVersion,
  };
};
